import { useEffect, useLayoutEffect, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Menu, SquarePen } from 'lucide-react';
import { ShibaWidget, summonShiba } from '@/components/Shiba/ShibaWidget';
import { ConversationSearchDialog, NEW_CONVERSATION_PATH } from '@/components/conversations';
import { useAuth } from '@/lib/auth';
import { useMe } from '@/lib/api';
import { SITE_NAME, usePageMeta } from '@/lib/seo';
import { cn, useFocusTrap, useIsNarrow } from '@/lib/utils';
import { ShellBarCenter, ShellBarProvider } from './shell-bar';
import { SideNav } from './SideNav';
import { UserMenu } from './UserMenu';
import './AppShell.css';

const COLLAPSE_KEY = 'habilita.nav-collapsed';

/**
 * Dentro do Conhecimento o chat da página já é a Shiba: o widget flutuante seria
 * um segundo chat do mesmo agente, por isso nem é montado ali.
 */
const KNOWLEDGE_PATH = '/app/conhecimento';
const CHAT_PATH = '/app/chat';

/** Alvo do `aria-controls` do botão que abre a gaveta. */
const NAV_ID = 'app-shell-nav';

/** Os ícones dos botões redondos da barra do topo, no traço dos da barra lateral. */
const BAR_ICON_PROPS = { size: 22, strokeWidth: 1.75 } as const;

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSE_KEY) === 'true';
  } catch {
    return false;
  }
}

/**
 * A moldura da área logada: a barra lateral e, ao lado dela, o conteúdo roteado
 * num painel arredondado, sem barra do topo no desktop. Cuida do recolher da
 * barra (salvo), da gaveta em telas estreitas e de "Procurar conversas", a busca
 * do console (Ctrl/Cmd-K). Nas telas estreitas o painel abre com a barra do topo
 * do app do Gemini ({@link ShellBar}), de onde a gaveta sai, e cujo centro é da
 * página ({@link ShellBarProvider}).
 */
export function AppShell(): JSX.Element {
  const { user } = useAuth();
  // O plano (capabilities do /v1/me) decide o que a navegação mostra: o eixo
  // comercial some com o que a conta não contratou, o eixo do papel segue no
  // requireRole de cada item. Sem `plan` na resposta (o backend anterior ao
  // lote, durante o rollout) nada some: sem plano, sem gating.
  const me = useMe();
  const planCapabilities = me.data?.plan?.capabilities;
  const location = useLocation();
  const navigate = useNavigate();
  const narrow = useIsNarrow();
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  // Estado, e não ref: a trava de foco depende do elemento (ver useFocusTrap).
  const [nav, setNav] = useState<HTMLDivElement | null>(null);
  const drawerActive = narrow && drawerOpen;
  const knowledgeRoute = isKnowledgeRoute(location.pathname);
  const chatRoute = location.pathname === CHAT_PATH || location.pathname.startsWith(`${CHAT_PATH}/`);
  const accountRoute = location.pathname === '/app/conta' || location.pathname.startsWith('/app/conta/');
  const widgetMounted = !chatRoute;
  // Sem o widget montado (Conhecimento), "Perguntar à Shiba" não teria quem
  // atender: os menus da conta (o da barra lateral e o da barra do topo) nem oferecem.
  const askShiba = widgetMounted ? summonShiba : undefined;

  // The whole authenticated console (/app/*) is kept out of search indexes.
  // No `title` here: each console page owns its own document.title — setting one
  // at the shell level would race the page effects (parent/child effect order
  // differs between SPA navigation and a hard reload).
  usePageMeta({
    description: `Área autenticada do ${SITE_NAME}.`,
    canonicalPath: '/app',
    noindex: true,
  });

  // Close the off-canvas drawer whenever the route changes.
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  // Estreita e fechada, a gaveta só saiu da TELA (translateX): continuaria na
  // ordem do Tab e na árvore acessível, com links invisíveis recebendo foco.
  // `inert` a tira das duas. Atributo, e não prop: o React 18 não conhece
  // `inert`. Efeito de layout para valer antes da pintura.
  useLayoutEffect(() => {
    nav?.toggleAttribute('inert', narrow && !drawerOpen);
  }, [nav, narrow, drawerOpen]);

  // Aberta, a gaveta é modal — o véu cobre a página: o foco entra nela, o Tab não
  // sai e, ao fechar, o foco volta para quem a abriu.
  useFocusTrap(nav, drawerActive);

  // Esc fecha a gaveta — a menos que o foco esteja em outra superfície aberta a
  // partir dela (um diálogo, um menu), que é dona do próprio Esc: uma tecla
  // nunca fecha duas coisas.
  useEffect(() => {
    if (!drawerActive) return;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') return;
      const focused = document.activeElement;
      if (focused && focused !== document.body && !nav?.contains(focused)) return;
      setDrawerOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drawerActive, nav]);

  // Persist the collapse preference.
  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSE_KEY, String(collapsed));
    } catch {
      // Non-fatal; preference simply won't survive reloads.
    }
  }, [collapsed]);

  // Ctrl/Cmd-K abre e fecha "Procurar conversas", a única busca do console. Com
  // Shift também: era o atalho dela enquanto o Ctrl K abria a paleta. A gaveta
  // aberta fica acima dos diálogos, então ela fecha antes, como no botão da barra.
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (!(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== 'k') return;
      event.preventDefault();
      setDrawerOpen(false);
      setSearchOpen((open) => !open);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Alt+Espaço abre o assistente. Na tela dedicada ao Chat o widget não existe,
  // então primeiro voltamos à Visão geral e só depois o chamamos.
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (!event.altKey || event.code !== 'Space') return;
      event.preventDefault();
      setDrawerOpen(false);
      if (chatRoute) {
        navigate('/app');
        window.setTimeout(summonShiba, 0);
      } else {
        summonShiba();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [chatRoute, navigate]);

  return (
    <div className={cn('app-shell', collapsed && 'app-shell--collapsed')}>
      <div ref={setNav} id={NAV_ID} className={cn('app-shell__nav', drawerOpen && 'app-shell__nav--open')}>
        <SideNav
          role={user?.role}
          planCapabilities={planCapabilities}
          collapsed={collapsed && !narrow}
          drawer={narrow}
          // Na gaveta (nunca recolhida) o controle da barra a fecha.
          onToggleCollapsed={narrow ? () => setDrawerOpen(false) : () => setCollapsed((v) => !v)}
          onNavigate={() => setDrawerOpen(false)}
          onOpenSearch={() => {
            // A conversa aberta pela busca já em /app/conhecimento só muda a
            // query: o fechamento por troca de rota não aconteceria e a gaveta
            // ficaria por cima do chat.
            setDrawerOpen(false);
            setSearchOpen(true);
          }}
          onAskShiba={askShiba}
        />
      </div>

      {drawerOpen && (
        <button
          type="button"
          className="app-shell__scrim"
          aria-label="Fechar navegação"
          onClick={() => setDrawerOpen(false)}
        />
      )}

      {/* O encaixe abraça a barra e a página: é a página quem diz o que vai no centro. */}
      <ShellBarProvider>
        <div className="app-shell__panel">
          {narrow && !accountRoute && (
            <ShellBar drawerOpen={drawerActive} onOpenDrawer={() => setDrawerOpen(true)} onAskShiba={askShiba} />
          )}
          <main className={cn('app-shell__content', (knowledgeRoute || chatRoute) && 'app-shell__content--knowledge')} id="conteudo">
            <Outlet />
          </main>
        </div>
      </ShellBarProvider>

      <ConversationSearchDialog open={searchOpen} onClose={() => setSearchOpen(false)} />

      {/* Assistente flutuante: disponível em toda a área autenticada, menos na
          tela dedicada ao Chat. Desmontado, e não escondido: ao voltar, ele relê
          do armazenamento se estava aberto. */}
      {widgetMounted && <ShibaWidget />}
    </div>
  );
}

function isKnowledgeRoute(pathname: string): boolean {
  return pathname === KNOWLEDGE_PATH || pathname.startsWith(`${KNOWLEDGE_PATH}/`);
}

interface ShellBarProps {
  drawerOpen: boolean;
  onOpenDrawer: () => void;
  /** Repassado ao menu da conta, como no rodapé da barra lateral. */
  onAskShiba?: () => void;
}

/**
 * A barra do topo das telas estreitas, no padrão do app do Gemini: o menu (≡)
 * que abre a gaveta à esquerda, no centro o que a página registrar (no chat, o
 * seletor de agente; vazio nas outras) e, à direita, "Novo chat" e o avatar, que
 * abre o mesmo menu da conta do rodapé da gaveta. Fica fora do <main>, então não
 * rola com o conteúdo.
 */
function ShellBar({ drawerOpen, onOpenDrawer, onAskShiba }: ShellBarProps): JSX.Element {
  return (
    <header className="app-shell__bar">
      <button
        type="button"
        className="app-shell__bar-button"
        aria-label="Abrir navegação"
        title="Abrir navegação"
        aria-expanded={drawerOpen}
        aria-controls={NAV_ID}
        onClick={onOpenDrawer}
      >
        <Menu {...BAR_ICON_PROPS} aria-hidden="true" />
      </button>
      <div className="app-shell__bar-center">
        <ShellBarCenter />
      </div>
      <div className="app-shell__bar-end">
        <Link to={NEW_CONVERSATION_PATH} className="app-shell__bar-button" aria-label="Novo chat" title="Novo chat">
          <SquarePen {...BAR_ICON_PROPS} aria-hidden="true" />
        </Link>
        <UserMenu collapsed avatarSize="md" onAskShiba={onAskShiba} />
      </div>
    </header>
  );
}
